import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { HERO_PHOTOS, IMG_LAGOON, CARNET_PHOTOS, PHOTO_NICHE, PHOTO_MERMAID } from "@/lib/assets";
import { ArrowRight, BookOpen, Clock, Users, MapPin, Backpack, ShieldCheck, Sun, Waves } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KidsCamp() {
  const { t, lang } = useLang();
  const k = t.kidscamp;
  const [cs, setCs] = useState({});
  useEffect(() => { axios.get(`${API}/kids-camp`).then((r) => setCs(r.data || {})).catch(() => {}); }, []);
  const ov = (field, fallback) => (cs[`${field}_${lang}`] && String(cs[`${field}_${lang}`]).trim()) ? cs[`${field}_${lang}`] : fallback;
  const themeItems = (cs.weeks && cs.weeks.length) ? cs.weeks.map((w) => ({ week: w[`week_${lang}`], emoji: w.emoji, name: w[`name_${lang}`], desc: w[`desc_${lang}`] })) : k.themes.items;
  return (
    <div data-testid="kidscamp-page" className="bg-[#fbf6ec]">
      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-end bg-[#6f7d54] overflow-hidden">
        <img src={PHOTO_MERMAID} alt="" className="absolute inset-0 w-full h-full object-cover kenburns" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#6f7d54] via-[#6f7d54]/45 to-[#6f7d54]/25" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10 pb-20 pt-32">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#e0b36a]/95 text-[#6f7d54] font-bold text-[13px] px-4 py-1.5 mb-5"><Sun size={15} /> {ov("hero_kicker", k.hero.kicker)}</span>
          <h1 className="font-head font-extrabold text-white text-[2.4rem] leading-[1.05] sm:text-6xl tracking-tight max-w-3xl">{ov("hero_title", k.hero.title)}</h1>
          <p className="font-body text-[#f7efe0]/90 text-lg mt-5 max-w-2xl">{ov("hero_subtitle", k.hero.subtitle)}</p>
          <Link to="/reservation" data-testid="kidscamp-hero-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] text-white font-bold text-lg px-8 py-4 transition-all hover:-translate-y-0.5 shadow-lg">
            {k.hero.cta} <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* CONCEPT */}
      <section className="grain relative py-20">
        <div className="relative z-10 max-w-4xl mx-auto px-5 lg:px-10 text-center">
          <h2 className="font-head font-bold text-[#6f7d54] text-3xl md:text-5xl tracking-tight mb-5">{ov("concept_title", k.concept.title)}</h2>
          <p className="font-body text-[#3a4d68] text-lg leading-relaxed">{ov("concept_text", k.concept.text)}</p>
        </div>
      </section>

      {/* NOTEBOOK */}
      <section className="bg-[#6f7d54] py-20 relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <img src={PHOTO_NICHE} alt="" className="w-full aspect-[4/3] object-cover rounded-[2rem] shadow-2xl -rotate-1" />
          </div>
          <div className="order-1 lg:order-2">
            <span className="font-script text-[#e0b36a] text-3xl">{k.notebook.kicker}</span>
            <h2 className="font-head font-bold text-white text-3xl md:text-4xl tracking-tight mt-1 mb-4 flex items-center gap-3"><BookOpen className="text-[#e0b36a]" /> {k.notebook.title}</h2>
            <p className="font-body text-[#f7efe0]/80 text-lg leading-relaxed">{k.notebook.text}</p>
          </div>
        </div>
      </section>

      {/* THEMES */}
      <section className="grain relative py-20">
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10">
          <h2 className="font-head font-bold text-[#6f7d54] text-3xl md:text-4xl tracking-tight mb-10 text-center">{ov("themes_title", k.themes.title)}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {themeItems.map((th, i) => (
              <div key={i} data-testid={`theme-${i}`} className="bg-white rounded-3xl border border-[#6f7d54]/10 p-7 hover:-translate-y-1 hover:shadow-xl transition-all">
                <span className="inline-block text-xs font-bold uppercase tracking-wide text-[#2e8b9e] bg-[#2e8b9e]/10 rounded-full px-3 py-1 mb-3">{th.week}</span>
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-4xl">{th.emoji}</span>
                  <h3 className="font-head font-bold text-[#6f7d54] text-lg">{th.name}</h3>
                </div>
                <p className="font-body text-[#3a4d68]/90 text-sm">{th.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCHEDULE + AGES */}
      <section className="bg-[#f1e6d2] py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-head font-bold text-[#6f7d54] text-2xl md:text-3xl tracking-tight mb-6 flex items-center gap-2"><Clock className="text-[#e07a5f]" /> {k.schedule.title}</h2>
            <div className="space-y-3">
              {k.schedule.items.map((s, i) => (
                <div key={i} className="flex gap-4 items-start bg-white rounded-2xl border border-[#6f7d54]/10 px-5 py-3">
                  <span className="font-head font-bold text-[#e07a5f] w-16 shrink-0">{s.time}</span>
                  <span className="font-body text-[#3a4d68]">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-head font-bold text-[#6f7d54] text-2xl md:text-3xl tracking-tight mb-6 flex items-center gap-2"><Users className="text-[#e07a5f]" /> {k.ages.title}</h2>
            <div className="space-y-3 mb-9">
              {k.ages.items.map((a, i) => (
                <div key={i} className="flex justify-between items-center bg-white rounded-2xl border border-[#6f7d54]/10 px-5 py-4">
                  <span className="font-head font-bold text-[#6f7d54]">{a.label}</span>
                  <span className="rounded-full bg-[#2e8b9e]/12 text-[#2e8b9e] font-bold text-sm px-3 py-1">{a.range}</span>
                </div>
              ))}
            </div>
            <h3 className="font-head font-bold text-[#6f7d54] text-xl mb-3">{k.options.title}</h3>
            <p className="font-body text-[#3a4d68] mb-3">{k.options.text}</p>
            <div className="flex flex-wrap gap-2">
              {[k.options.morning, k.options.afternoon, k.options.fullday].map((o, i) => (
                <span key={i} className="rounded-full bg-white border border-[#6f7d54]/10 text-[#6f7d54] font-semibold text-sm px-4 py-2">{o}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="grain relative py-20">
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-head font-bold text-[#6f7d54] text-3xl md:text-4xl tracking-tight mb-4 flex items-center gap-2"><MapPin className="text-[#e07a5f]" /> {k.location.title}</h2>
            <p className="font-body text-[#3a4d68] text-lg leading-relaxed">{k.location.text}</p>
          </div>
          <img src={IMG_LAGOON} alt="" className="w-full aspect-[4/3] object-cover rounded-[2rem] shadow-2xl" />
        </div>
      </section>

      {/* BRING + SAFETY */}
      <section className="bg-[#6f7d54] py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid md:grid-cols-2 gap-8">
          <div className="bg-white/8 border border-white/10 rounded-3xl p-8">
            <h3 className="font-head font-bold text-white text-2xl mb-5 flex items-center gap-2"><Backpack className="text-[#e0b36a]" /> {k.bring.title}</h3>
            <ul className="space-y-3">{k.bring.items.map((b, i) => <li key={i} className="font-body text-[#f7efe0]/85 flex items-center gap-2"><Waves size={15} className="text-[#e0b36a]" /> {b}</li>)}</ul>
          </div>
          <div className="bg-white/8 border border-white/10 rounded-3xl p-8">
            <h3 className="font-head font-bold text-white text-2xl mb-5 flex items-center gap-2"><ShieldCheck className="text-[#e0b36a]" /> {k.safety.title}</h3>
            <ul className="space-y-3">{k.safety.items.map((b, i) => <li key={i} className="font-body text-[#f7efe0]/85 flex items-center gap-2"><ShieldCheck size={15} className="text-[#9ec48a]" /> {b}</li>)}</ul>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="grain relative py-20">
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10">
          <h2 className="font-head font-bold text-[#6f7d54] text-3xl md:text-4xl tracking-tight mb-10 text-center">{k.gallery.title}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {CARNET_PHOTOS.map((p, i) => (
              <img key={i} src={p.src} alt="" className={`w-full aspect-square object-cover rounded-2xl shadow-lg ${i % 2 ? "rotate-1" : "-rotate-1"} hover:rotate-0 transition-transform`} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/reservation" data-testid="kidscamp-cta" className="inline-flex items-center gap-2 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] text-white font-bold text-lg px-8 py-4 transition-all hover:-translate-y-0.5">
              {k.hero.cta} <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
